// Pure preview helper mirroring backend `compute_split` (services/contracts/split.py).
// Intermediate math runs at full float precision; only the output boundary is
// truncated to cents via `money()` (espelha `_money` — quantize ROUND_DOWN, trunca
// no centavo a favor do pagador). `caixa_td` usa a soma de bruto NÃO truncada, igual
// ao BE. Não é exato como Decimal, mas elimina a divergência de arredondamento do
// preview. Pricing fields are fractions (0..1) as strings; `recuperado` em BRL.

export interface CascadeIndicacaoInput {
  label: string;
  percent: string; // fraction 0..1
  taxa_imposto_pct: string; // fraction 0..1
}

export interface CascadeIndicacaoResult {
  label: string;
  bruto: number; // td_recebe * percent
  imposto: number; // bruto * taxa
  liquido: number; // bruto * (1 - taxa)
}

export interface CascadeResult {
  recuperado: number;
  operador: number; // recuperado * success_fee
  td_recebe: number; // operador * td_share
  cliente: number; // recuperado - operador
  indicacoes: CascadeIndicacaoResult[];
  caixa_td: number; // td_recebe - Σ bruto
}

function frac(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Mirror backend `_money`: quantize to cents rounding DOWN (truncate toward zero).
// Math.trunc matches Decimal ROUND_DOWN for both signs; round() before trunc absorbs
// binary-float noise (ex.: 0.1*3*100 = 30.000000000000004) so o último centavo bate.
function money(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.trunc(Math.round(value * 100 * 1e6) / 1e6) / 100;
}

export function computeCascade(args: {
  recuperado: number;
  success_fee_pct: string;
  td_share_pct: string;
  indicacoes: CascadeIndicacaoInput[];
}): CascadeResult {
  const recuperado = Number.isFinite(args.recuperado) ? args.recuperado : 0;
  // Full-precision intermediates (igual ao BE — só o boundary é truncado).
  const operador = recuperado * frac(args.success_fee_pct);
  const td_recebe = operador * frac(args.td_share_pct);

  let somaBruto = 0;
  const indicacoes = args.indicacoes.map((ind) => {
    const bruto = td_recebe * frac(ind.percent);
    const taxa = frac(ind.taxa_imposto_pct);
    const imposto = bruto * taxa;
    const liquido = bruto - imposto;
    somaBruto += bruto;
    return {
      label: ind.label,
      bruto: money(bruto),
      imposto: money(imposto),
      liquido: money(liquido),
    };
  });

  return {
    recuperado: money(recuperado),
    operador: money(operador),
    td_recebe: money(td_recebe),
    cliente: money(recuperado - operador),
    indicacoes,
    caixa_td: money(td_recebe - somaBruto),
  };
}
